"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Votre test prouve-t-il quelque chose ?",
        visitors: "Visiteurs par version",
        base: "Taux de la version A",
        lift: "Écart observé sur la version B",
        needed: "Visiteurs nécessaires par version",
        verdictOk: "L'écart est assez net pour être distingué du hasard.",
        verdictNo: "À ce volume, cet écart est indiscernable du hasard. Un gagnant désigné ici serait un faux gagnant.",
        verdictClose: "C'est limite. Laissez tourner jusqu'au volume nécessaire avant de trancher.",
        have: "Vous avez",
        perVersion: "par version",
        note: "Le seuil est calculé pour un test bilatéral à 95 % de confiance et 80 % de puissance, la convention courante. Il ne remplace pas un outil statistique, il donne l'ordre de grandeur, qui suffit presque toujours à savoir s'il faut attendre.",
    },
    en: {
        title: "Does your test prove anything?",
        visitors: "Visitors per version",
        base: "Version A rate",
        lift: "Gap observed on version B",
        needed: "Visitors needed per version",
        verdictOk: "The gap is clear enough to be distinguished from chance.",
        verdictNo: "At this volume that gap is indistinguishable from chance. A winner declared here would be a false winner.",
        verdictClose: "It is borderline. Let it run to the required volume before deciding.",
        have: "You have",
        perVersion: "per version",
        note: "The threshold is calculated for a two-sided test at 95 percent confidence and 80 percent power, the common convention. It does not replace a statistical tool, it gives the order of magnitude, which is nearly always enough to know whether to wait.",
    },
};

export default function AbTestSignificance({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [visitors, setVisitors] = useState(500);
    const [base, setBase] = useState(3);
    const [lift, setLift] = useState(0.6);

    const r = useMemo(() => {
        const p1 = base / 100;
        const p2 = (base + lift) / 100;
        const delta = Math.abs(p2 - p1);

        // Taille d'échantillon par groupe, approximation classique :
        // n = (z(a/2) + z(b))² × (p1(1-p1) + p2(1-p2)) / delta²
        // avec z = 1,96 et 0,84 -> (1,96 + 0,84)² = 7,849
        const needed = delta > 0 ? (7.849 * (p1 * (1 - p1) + p2 * (1 - p2))) / delta ** 2 : Infinity;
        return { needed, ratio: visitors / needed };
    }, [visitors, base, lift]);

    const tone = r.ratio >= 1 ? "good" : r.ratio >= 0.7 ? "warn" : "bad";
    const message = r.ratio >= 1 ? t.verdictOk : r.ratio >= 0.7 ? t.verdictClose : t.verdictNo;
    const fmt = (n) =>
        Number.isFinite(n)
            ? Math.ceil(n).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")
            : "∞";

    return (
        <Frame icon="minimalistic-magnifer-linear" title={t.title}>
            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-4">
                    <Slider
                        label={t.visitors}
                        value={visitors}
                        onChange={setVisitors}
                        min={100}
                        max={50000}
                        step={100}
                        display={visitors.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
                    />
                    <Slider label={t.base} value={base} onChange={setBase} min={1} max={30} step={0.5} display={`${base} %`} />
                    <Slider label={t.lift} value={lift} onChange={setLift} min={0.2} max={10} step={0.2} display={`+${lift.toFixed(1)} pt`} />
                </div>

                <div className="flex flex-col justify-center rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-5">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.needed}</div>
                    <div className="text-4xl font-bold text-primary tabular-nums my-1">{fmt(r.needed)}</div>
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                        {t.have} {visitors.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} {t.perVersion}
                    </div>

                    <div className="h-2.5 rounded-full bg-gray-200/70 dark:bg-white/10 overflow-hidden mt-3">
                        <div
                            className={`h-full rounded-full ${r.ratio >= 1 ? "bg-green-600" : r.ratio >= 0.7 ? "bg-amber-500" : "bg-red-500"}`}
                            style={{ width: `${Math.min(100, r.ratio * 100)}%` }}
                        />
                    </div>
                </div>
            </div>

            <Verdict tone={tone}>{message}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
